import React, { useRef } from "react";
import { styled } from "styled-components";
import {
  FieldChange,
  EMPTY_DISPLAY,
  formatAttr,
  formatDuration,
} from "./configModel";
import { SchemaField } from "./configTypes";
import { tokens } from "./tokens";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { wallTimeToIso, wallTimeValue } from "../TractorMetrics/time";

/**
 * Confirm an edit before it is written.
 *
 * A save here changes what a tractor is cleared to do, so the last thing before
 * the write is an explicit before/after of every field that will move — the
 * spreadsheet this replaces had no such step, and no record afterwards either.
 * The list shown is exactly the change-set that will be sent and stored, not a
 * summary of it.
 *
 * Two more things ride on the save (SW-8879). *When* the change took effect:
 * records lag reality — when something happens on the apron the person comes
 * first and the tractor's record is updated later — so the time defaults to now
 * and may be set earlier, in which case Tractor Metrics rewrites the days it
 * touches. And *why*: a note that shows on the timeline and in the history. A
 * change to a readiness-driving field cannot be saved without it, because that
 * change becomes a red or green segment that has to say why.
 *
 * The way out is "Back to editing", not "Cancel": the page behind this box has
 * its own Cancel that discards the draft, and two buttons with one label and
 * opposite consequences was the complaint. This one keeps everything.
 */
export interface ReviewDialogProps {
  changes: FieldChange[];
  /**
   * Changes a cross-field rule will make because of `changes` — not asked for,
   * not sent, applied by the API. Listed under their own heading so the save
   * is explicit about every field that will move, including the ones the
   * operator did not touch.
   */
  cascades?: FieldChange[];
  /** Attestations being re-affirmed at their current value. */
  confirmations: SchemaField[];
  vehicleLabel: string;
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** When the change took effect: ISO 8601 with an offset, or null for now. */
  effectiveAt: string | null;
  onEffectiveAtChange: (iso: string | null) => void;
  note: string;
  onNoteChange: (note: string) => void;
  /** True when a readiness-driving field is in the change-set. */
  noteRequired: boolean;
  /** The tractor's zone, which the time picker works in. */
  tz: string;
  /**
   * When a later recorded change of a touched readiness field exists, its
   * time: the save then rewrites the past and leaves today's values alone.
   */
  laterChangeAt: string | null;
}

/** A time is "in the past" once it is more than a minute behind now. */
const PAST_GRACE_MS = 60_000;

function describeWhen(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const ReviewDialog: React.FC<ReviewDialogProps> = ({
  changes,
  cascades = [],
  confirmations,
  vehicleLabel,
  saving,
  error,
  onConfirm,
  onCancel,
  effectiveAt,
  onEffectiveAtChange,
  note,
  onNoteChange,
  noteRequired,
  tz,
  laterChangeAt,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const total = changes.length + confirmations.length + cascades.length;
  const count = `${total} change${total === 1 ? "" : "s"}`;
  const noteMissing = noteRequired && !note.trim();
  const earlier = effectiveAt !== null;
  const behindMs = earlier ? Date.now() - Date.parse(effectiveAt) : 0;
  const inThePast = earlier && behindMs > PAST_GRACE_MS;

  const pickEarlier = () => {
    if (earlier) return;
    // Start the picker at the current minute in the tractor's zone; the
    // operator then moves it back.
    const now = new Date();
    now.setSeconds(0, 0);
    onEffectiveAtChange(
      wallTimeToIso(wallTimeValue(now.toISOString(), tz), tz),
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !saving) onCancel();
      }}
    >
      <DialogContent
        // The list *is* the description; a second sentence would only repeat
        // the title.
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          // Land on the primary action: the reader has already reviewed the
          // list on the page, and Enter should complete the save.
          e.preventDefault();
          confirmRef.current?.focus();
        }}
        onEscapeKeyDown={(e) => {
          if (saving) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (saving) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            Save {count} to {vehicleLabel}?
          </DialogTitle>
        </DialogHeader>

        <List>
          {changes.map(({ field, from, to }) => (
            <Item key={field.name}>
              <ItemLabel>{field.label}</ItemLabel>
              <Transition>
                <From>{formatAttr(field, from) || EMPTY_DISPLAY}</From>
                <Arrow aria-hidden="true">→</Arrow>
                <SrOnly>becomes</SrOnly>
                <To>{formatAttr(field, to) || EMPTY_DISPLAY}</To>
              </Transition>
              {field.sheet_sync_owned && (
                <Warning>
                  Still synced from the Google Sheet every 15 minutes — this
                  edit can be overwritten until that sync is retired.
                </Warning>
              )}
            </Item>
          ))}
          {cascades.length > 0 && (
            <RuleHeading>
              Applied by rule
              <RuleHelp>
                The API makes these changes because of the ones above; they are
                not sent from here.
              </RuleHelp>
            </RuleHeading>
          )}
          {cascades.map(({ field, from, to, rule }) => (
            <Item key={`rule-${field.name}`}>
              <ItemLabel>{field.label}</ItemLabel>
              <Transition>
                <From>{formatAttr(field, from) || EMPTY_DISPLAY}</From>
                <Arrow aria-hidden="true">→</Arrow>
                <SrOnly>becomes</SrOnly>
                <To>{formatAttr(field, to) || EMPTY_DISPLAY}</To>
              </Transition>
              {rule && <Note>{rule}</Note>}
            </Item>
          ))}
          {confirmations.map((field) => (
            <Item key={`confirm-${field.name}`}>
              <ItemLabel>{field.label}</ItemLabel>
              <Transition>
                <To>Re-confirmed at its current value</To>
              </Transition>
              <Note>
                Records that this was checked again today and the answer is
                unchanged. The value is not modified.
              </Note>
            </Item>
          ))}

          <WhenBox>
            <WhenTitle>When did this take effect?</WhenTitle>
            <WhenRow>
              <Segmented role="group" aria-label="Effective time">
                <SegmentButton
                  type="button"
                  aria-pressed={!earlier}
                  onClick={() => onEffectiveAtChange(null)}
                >
                  Now
                </SegmentButton>
                <SegmentButton
                  type="button"
                  aria-pressed={earlier}
                  onClick={pickEarlier}
                >
                  Earlier
                </SegmentButton>
              </Segmented>
              {earlier && (
                <>
                  <SrOnly as="label" htmlFor="review-effective-at">
                    Effective date and time
                  </SrOnly>
                  <TimeInput
                    id="review-effective-at"
                    type="datetime-local"
                    value={wallTimeValue(effectiveAt, tz)}
                    max={wallTimeValue(new Date().toISOString(), tz)}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      try {
                        onEffectiveAtChange(wallTimeToIso(e.target.value, tz));
                      } catch {
                        // An incomplete value while typing; keep the last one.
                      }
                    }}
                  />
                  <Zone>{zoneAbbrev(tz)} · the tractor&apos;s zone</Zone>
                </>
              )}
            </WhenRow>
            {inThePast && (
              <PastLine role="status">
                This is {formatDuration(behindMs / 1000)} in the past. Tractor
                Metrics rewrites {vehicleLabel} from{" "}
                {describeWhen(effectiveAt, tz)}.{" "}
                {laterChangeAt
                  ? "Today's values stay as they are: a later change already covers this field."
                  : "Today's values become the ones above."}
              </PastLine>
            )}
          </WhenBox>

          <NoteField>
            <NoteLabel htmlFor="review-note">
              Note · shows on the timeline and in the history
            </NoteLabel>
            <NoteInput
              id="review-note"
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="What happened, in one or two sentences."
            />
            {noteRequired && (
              <NoteHelp>
                Required: this change moves production readiness, and the note
                is what the timeline shows for it.
              </NoteHelp>
            )}
          </NoteField>
        </List>

        {error && <ErrorBox role="alert">{error}</ErrorBox>}

        <DialogFooter>
          <Button
            variant="ghost"
            size="md"
            onClick={onCancel}
            disabled={saving}
          >
            Back to editing
          </Button>
          <Button
            ref={confirmRef}
            variant="primary"
            size="md"
            onClick={onConfirm}
            disabled={saving || noteMissing}
          >
            {saving ? "Saving…" : `Save ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** A short zone label ("EDT") for the picker. */
function zoneAbbrev(tz: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? tz;
  } catch {
    return tz;
  }
}

/** The scrolling region: the title and the buttons stay put around it. */
const List = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.div`
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  padding: 10px 12px;
`;

const ItemLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.slate};
  margin-bottom: 5px;
`;

const Transition = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13.5px;
`;

const From = styled.span`
  color: ${tokens.slate};
  text-decoration: line-through;
`;

const Arrow = styled.span`
  color: ${tokens.slate};
`;

/** Read aloud in place of the arrow glyph, which a screen reader would skip. */
const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const To = styled.span`
  font-weight: 600;
  color: ${tokens.ink};
`;

const Note = styled.div`
  margin-top: 7px;
  font-size: 11.5px;
  color: ${tokens.slate};
`;

const Warning = styled.div`
  margin-top: 7px;
  font-size: 11.5px;
  color: ${tokens.majorText};
`;

const RuleHeading = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.ink};
`;

const RuleHelp = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 400;
  color: ${tokens.slate};
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  border: 1px solid rgba(229, 72, 77, 0.35);
  background: rgba(229, 72, 77, 0.08);
  color: ${tokens.groundedText};
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
`;

const WhenBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  background: ${tokens.offWhite};
`;

const WhenTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.ink};
`;

const WhenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${tokens.inputLine};
  border-radius: 8px;
  overflow: hidden;
  background: ${tokens.white};
`;

const SegmentButton = styled.button`
  border: 0;
  background: ${tokens.white};
  padding: 8px 14px;
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.slate};
  cursor: pointer;
  min-height: 40px;
  &[aria-pressed="true"] {
    background: ${tokens.navy};
    color: ${tokens.white};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
  }
`;

const TimeInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${tokens.inputLine};
  border-radius: 6px;
  background: ${tokens.white};
  color: ${tokens.ink};
  font: 14px ${tokens.fontSans};
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
  }
`;

const Zone = styled.span`
  font-size: 12px;
  color: ${tokens.slate};
`;

const PastLine = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${tokens.majorText};
`;

const NoteField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NoteLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${tokens.slate};
`;

const NoteInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid ${tokens.inputLine};
  border-radius: 6px;
  background: ${tokens.white};
  color: ${tokens.ink};
  font: 14px ${tokens.fontSans};
  resize: vertical;
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
  }
`;

const NoteHelp = styled.div`
  font-size: 12px;
  color: ${tokens.slate};
`;

export default ReviewDialog;
